import { useEffect, useState } from 'react'
import { PermissionAction, permissionsService, PermissionType } from '../services/permissions.service'
import { workflowStatusService } from '../services/workflow-status.service'
import { WorkflowStatus } from '../types'

let globalCache: WorkflowStatus[] | null = null
let globalCachePromise: Promise<WorkflowStatus[]> | null = null
const cacheListeners: Set<() => void> = new Set()

const loadCache = async (): Promise<WorkflowStatus[]> => {
  if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.WorkflowStatus)) {
    return []
  }
  if (globalCache) {
    return globalCache
  }

  if (globalCachePromise) {
    return globalCachePromise
  }

  globalCachePromise = workflowStatusService
    .listAll()
    .then((response) => {
      globalCache = response.results || []
      globalCachePromise = null
      cacheListeners.forEach((listener) => listener())
      return globalCache
    })
    .catch(() => {
      globalCachePromise = null
      return []
    })

  return globalCachePromise
}

export const useWorkflowStatusesCache = (): { workflowStatuses: WorkflowStatus[]; loaded: boolean } => {
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>(globalCache || [])
  const [loaded, setLoaded] = useState(!!globalCache)

  useEffect(() => {
    if (globalCache) {
      setWorkflowStatuses(globalCache)
      setLoaded(true)
      return
    }

    const listener = () => {
      if (globalCache) {
        setWorkflowStatuses(globalCache)
        setLoaded(true)
      }
    }

    cacheListeners.add(listener)

    loadCache().then((cached) => {
      setWorkflowStatuses(cached)
      setLoaded(true)
    })

    return () => {
      cacheListeners.delete(listener)
    }
  }, [])

  return { workflowStatuses, loaded }
}

export const clearWorkflowStatusesCache = (): void => {
  globalCache = null
  globalCachePromise = null
  workflowStatusService.clearCache()
  cacheListeners.forEach((listener) => listener())
}
